/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import { CarbonDetailSiteField } from '../Utils/Constants';
import CommonProgressBar from '../CommonProgressBar/CommonProgressBar';
import { IDataTableColumn } from '../MeterSavingsDetailTable/IMeterSavingsDetailTableProps';
import './CarbonDetailTabBreakdownBySiteTheme.scss';
import { ICarbonDetailTabBreakdownBySiteProps } from './ICarbonDetailTabBreakdownProps';
import { getAbsoluteValue } from '../Utils/Utils';
import {
  getSavingsBadgeColor,
  apiCallAtCheck
} from '../Utils/CarbonViewDetailsUtils';
import TruncateText from '../TruncatedText/TruncateText';
import { ApiContext } from '../../../context/ApiContext';
import {
  IAny,
  ISiteSavingsData
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';
import { ConversionType } from '../Utils/unitHandling/UnitModel';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';

const CarbonDetailTabBreakdownBySite = (
  props: ICarbonDetailTabBreakdownBySiteProps
) => {
  const apiContext = useContext(ApiContext);
  const { onPortfolioSiteClick, apiCallAt } = apiContext;
  const onSiteclick = (data: ISiteSavingsData) => {
    if (data && data.siteId) {
      if (onPortfolioSiteClick) {
        onPortfolioSiteClick(data.siteId);
      }
    }
  };
  const clsName = apiCallAtCheck(apiCallAt);

  const { meterData, utilityMeasurementUnit, columnDetails } = props;

  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(
    CarbonDetailSiteField.actualConsumption
  );
  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    const MAX_ROW_LIMIT = 5;
    if (field === CarbonDetailSiteField.site) {
      return (
        <span
          className={clsName}
          onClick={() => {
            onSiteclick(rowData);
          }}
        >
          <span className="score-category-percent">
            {value && value.length <= MAX_ROW_LIMIT ? (
              <div className="measurementunit">
                <TruncateText text={value} />
              </div>
            ) : (
              <div>{value}</div>
            )}
          </span>
        </span>
      );
    }
    if (field === CarbonDetailSiteField.actualConsumption) {
      return (
        <span className="emaas-carbondetail-category-meterdata">
          <span className="score-category-percent">
            <div className="measurementunit">
              {
                getConvertedValue(
                  ConversionType.CARBON,
                  props.emissionMeasurementUnit
                    ? props.emissionMeasurementUnit
                    : '',
                  rowData.actualConsumption,
                  rowData.actualUnit
                ).Value
              }
            </div>
            {/* {utilityMeasurementUnit} */}
            {rowData.actualUnit}
          </span>
          <span className="score-category-progress">
            <CommonProgressBar
              actual={rowData.actualConsumption}
              previousPeriod={rowData.baselineConsumption}
              savings={rowData.change}
              savingsPercentage={rowData.percentageConsumptionSaving}
              progessBarRelativeWidth={rowData.progessBarRelativeWidth}
            />
          </span>
        </span>
      );
    }
    if (field === CarbonDetailSiteField.change) {
      const changeValue: IAny = getConvertedValue(
        ConversionType.CARBON,
        props.emissionMeasurementUnit ? props.emissionMeasurementUnit : '',
        rowData.change,
        rowData.changeUnit
      ).Value;
      return (
        <span className="score-category-meterdata-meter">
          <span
            className="savings-badge"
            style={{
              color: getSavingsBadgeColor(changeValue)
            }}
          >
            {value === null ? '--' : getAbsoluteValue(changeValue)}
            {value !== null &&
              ' ' +
                rowData.changeUnit +
                ' ' +
                '(' +
                getAbsoluteValue(rowData.percentageConsumptionSaving) +
                '%' +
                ')'}
          </span>
        </span>
      );
    }

    if (field === CarbonDetailSiteField.baselineConsumption) {
      return (
        <span className="score-category-meterdata-meter">
          <div className="consumptionvalue">
            {
              getConvertedValue(
                ConversionType.CARBON,
                props.emissionMeasurementUnit
                  ? props.emissionMeasurementUnit
                  : '',
                rowData.baselineConsumption,
                rowData.baselineUnit
              ).Value
            }
          </div>
          {/* {utilityMeasurementUnit} */}
          {rowData.baselineUnit}
        </span>
      );
    }

    return <></>;
  };

  return (
    <DataTable
      className="Savingtable"
      data={meterData}
      onSort={({ sortField, sortOrder }) => {
        setFieldSortOrder(sortOrder as number);
        setSortFieldName(sortField);
      }}
      sortField={sortFieldName}
      sortOrder={fieldSortOrder}
    >
      {columnDetails.map((column: IDataTableColumn) => {
        return (
          <DataTable.Column
            field={column.field}
            header={column.header}
            sortable={column.sortable}
            renderer={cellDataRenderer}
            className={column.className}
            initialWidth={column.initialWidth}
          />
        );
      })}
    </DataTable>
  );
};
export default CarbonDetailTabBreakdownBySite;
